const BRAND_LOGO_URL = `${window.location.origin}/brand/celsius-dark.png`;

/**
 * Client-side branded invoice print (HTML → blob → new tab → window.print).
 * Extracted from Invoices.jsx to keep the page shell lean.
 */
export function printInvoice(inv, company) {
  const c = company || {};
  const currency = localStorage.getItem("hvac_currency") || "INR";
  const fmt = (n) => {
    try {
      return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
        style: "currency", currency, maximumFractionDigits: 2,
      }).format(Number(n || 0));
    } catch {
      return `${currency} ${Number(n || 0).toFixed(2)}`;
    }
  };
  const esc = (v) => String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  const addressParts = [
    c.address,
    [c.city, c.state, c.pincode].filter(Boolean).join(", "),
    c.phone && `Ph: ${c.phone}`, c.email, c.website,
  ].filter(Boolean);

  const html = `<!doctype html><html><head><title>${esc(inv.invoice_number)}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:'IBM Plex Sans','Helvetica Neue',sans-serif;padding:36px;color:#0F172A;margin:0}
      h1{font-family:'Outfit',sans-serif;margin:0;letter-spacing:-0.02em}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0F172A;padding-bottom:20px}
      .brand{display:flex;gap:14px;align-items:center}
      .brand-logo{width:56px;height:56px;background:#0F172A;color:#EA580C;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:800;font-size:24px}
      .brand-name{font-family:'Outfit',sans-serif;font-weight:700;font-size:20px}
      .brand-tag{font-size:11px;color:#64748B;letter-spacing:0.15em;text-transform:uppercase}
      .doc-title{text-align:right}
      .doc-title h1{font-size:28px}
      .doc-num{color:#64748B;font-size:11px;letter-spacing:0.2em;text-transform:uppercase;margin-top:4px}
      .accent{color:#EA580C}
      .meta{display:flex;justify-content:space-between;margin-top:24px;gap:24px}
      .col{flex:1}
      .label{font-size:10px;text-transform:uppercase;color:#64748B;letter-spacing:0.18em;font-weight:600;margin-bottom:4px}
      .val{font-size:13px}
      table{width:100%;border-collapse:collapse;margin:24px 0}
      thead th{background:#0F172A;color:#fff;text-align:left;padding:10px;font-size:10px;text-transform:uppercase;letter-spacing:0.15em;font-weight:600}
      tbody td{border-bottom:1px solid #E2E8F0;padding:10px;font-size:13px}
      .right{text-align:right}
      .totals{margin-left:auto;width:280px}
      .totals .row{display:flex;justify-content:space-between;padding:6px 0;font-size:13px}
      .totals .total{border-top:2px solid #0F172A;margin-top:6px;padding-top:8px;font-family:'Outfit',sans-serif;font-weight:700;font-size:20px;color:#EA580C}
      .foot{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:32px;padding-top:20px;border-top:1px solid #E2E8F0}
      .box{background:#F8FAFC;border:1px solid #E2E8F0;padding:14px}
      .status{display:inline-block;padding:3px 8px;font-size:10px;text-transform:uppercase;letter-spacing:0.15em;font-weight:600;background:#F1F5F9;color:#0F172A}
      .status.paid{background:#DCFCE7;color:#166534}
      .status.partial{background:#FEF3C7;color:#92400E}
      .status.overdue{background:#FEE2E2;color:#991B1B}
      .signature{margin-top:60px;border-top:1px solid #0F172A;padding-top:6px;width:220px;font-size:11px;text-transform:uppercase;letter-spacing:0.15em;color:#64748B}
      @media print { body { padding: 20px } }
    </style></head><body>
    <div class="header">
      <div class="brand">
        <img src="${esc(c.logo_url || BRAND_LOGO_URL)}" style="max-height:64px;width:auto" alt="Celsius" />
        <div>
        ${c.tagline ? `<div class="brand-tag">${esc(c.tagline)}</div>` : ""}
          ${c.gstin ? `<div style="font-size:11px;color:#334155;margin-top:4px"><strong>GSTIN:</strong> ${esc(c.gstin)}</div>` : ""}
        </div>
      </div>
      <div class="doc-title">
        <h1>TAX <span class="accent">INVOICE</span></h1>
        <div class="doc-num">${esc(inv.invoice_number)}</div>
        <div style="margin-top:8px"><span class="status ${esc(inv.status)}">${esc(inv.status)}</span></div>
      </div>
    </div>

    <div class="meta">
      <div class="col">
        <div class="label">Billed From</div>
        <div class="val"><strong>${esc(c.name || "")}</strong></div>
        ${addressParts.map((p) => `<div class="val" style="color:#475569">${esc(p)}</div>`).join("")}
      </div>
      <div class="col">
        <div class="label">Billed To</div>
        <div class="val"><strong>${esc(inv.client_name)}</strong></div>
      </div>
      <div class="col">
        <div class="label">Issue Date</div>
        <div class="val">${esc(inv.issue_date)}</div>
        <div class="label" style="margin-top:10px">Due Date</div>
        <div class="val">${esc(inv.due_date)}</div>
      </div>
    </div>

    <table>
      <thead><tr><th>#</th><th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">Amount</th></tr></thead>
      <tbody>${inv.items.map((i, idx) => `<tr><td>${idx + 1}</td><td>${esc(i.description || "—")}${i.hsn ? ` <span style="color:#64748B;font-size:10px">HSN ${esc(i.hsn)}</span>` : ""}</td><td class="right">${esc(i.qty)}</td><td class="right">${fmt(i.unit_price)}</td><td class="right">${fmt(Number(i.qty) * Number(i.unit_price))}</td></tr>`).join("")}</tbody>
    </table>

    <div style="display:flex;gap:24px">
      <div style="flex:1"></div>
      <div class="totals">
        <div class="row"><span>Subtotal</span><span>${fmt(inv.subtotal)}</span></div>
        ${inv.is_interstate
          ? `<div class="row"><span>IGST</span><span>${fmt(inv.igst_amount)}</span></div>`
          : `<div class="row"><span>CGST</span><span>${fmt(inv.cgst_amount)}</span></div>
             <div class="row"><span>SGST</span><span>${fmt(inv.sgst_amount)}</span></div>`}
        <div class="row total"><span>TOTAL DUE</span><span>${fmt(inv.total)}</span></div>
        <div class="row" style="color:#16A34A"><span>Paid</span><span>${fmt(inv.amount_paid)}</span></div>
        <div class="row" style="font-weight:700"><span>Balance</span><span>${fmt(inv.total - inv.amount_paid)}</span></div>
      </div>
    </div>

    <div class="foot">
      <div>
        ${(c.bank_name || c.bank_account) ? `<div class="box"><div class="label">Bank Details</div>
          ${c.bank_name ? `<div class="val"><strong>Bank:</strong> ${esc(c.bank_name)}</div>` : ""}
          ${c.bank_account ? `<div class="val"><strong>A/c:</strong> ${esc(c.bank_account)}</div>` : ""}
          ${c.bank_ifsc ? `<div class="val"><strong>IFSC:</strong> ${esc(c.bank_ifsc)}</div>` : ""}
        </div>` : ""}
        ${inv.notes ? `<div class="box" style="margin-top:12px"><div class="label">Notes</div><div class="val">${esc(inv.notes)}</div></div>` : ""}
      </div>
      <div style="display:flex;flex-direction:column;justify-content:space-between">
        <div class="box"><div class="label">Terms</div><div class="val">Payment due within terms above. Thank you for your business.</div></div>
        <div class="signature">Authorized Signatory</div>
      </div>
    </div>
    <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
    </body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    window.location.href = url;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
